import React, { Suspense, useEffect, useMemo } from 'react';

import { loadEntryPoint, useRelayEnvironment } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	AgentProfileCardResourced,
	type AgentProfileCardResourcedProps,
} from '@atlaskit/profilecard/agent-profile-card-resourced';
import { agentProfileCardEntryPoint } from '@atlassian/conversation-assistant-agent/common/ui/agent-profile-card-entrypoint';
import type { AgentProfileCardRuntimeProps } from '@atlassian/conversation-assistant-agent/common/ui/agent-profile-card-types';
import { AgentProfileCardSkeleton } from '@atlassian/conversation-assistant-agent/common/ui/AgentProfileCardSkeleton';
import { InternalEntryPointContainer } from '@atlassian/internal-entry-point-container';

export type AgentCardProps = AgentProfileCardResourcedProps &
	Omit<
		AgentProfileCardRuntimeProps,
		'cloudId' | 'identityAccountId' | 'onConversationStarterClick' | 'onDelete'
	>;

function AgentCardRelay({
	accountId,
	cloudId,
	hideStarButton,
	hideConversationStarters,
	onChatClick,
	onConversationStartersClick,
	onViewProfile,
	onDuplicate,
	onCopyLink,
	onEdit,
	onDeleteAgent,
	renderCTA,
	renderMoreActions,
}: AgentCardProps): React.JSX.Element {
	const environment = useRelayEnvironment();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const entryPointReference = useMemo(
		() =>
			loadEntryPoint({ getEnvironment: () => environment }, agentProfileCardEntryPoint, {
				identityAccountId: accountId,
				cloudId,
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[accountId, cloudId],
	);

	const handleConversationStarterClick = useMemo(
		() => (prompt: string) =>
			onConversationStartersClick?.({ message: prompt, type: 'static' }),
		[onConversationStartersClick],
	);

	const handleDelete = useMemo(
		() =>
			onDeleteAgent
				? (agentId: string) => {
						onDeleteAgent(agentId);
					}
				: undefined,
		[onDeleteAgent],
	);

	useEffect(() => {
		createAnalyticsEvent({
			action: 'mounted',
			actionSubject: 'agentCardRelay',
			eventType: 'operational',
			attributes: {
				source: 'agentCard',
			},
		}).fire();
		return () => {
			entryPointReference.dispose?.();
		};
	}, [entryPointReference, createAnalyticsEvent]);

	return (
		<Suspense fallback={<AgentProfileCardSkeleton />}>
			<InternalEntryPointContainer
				id="agent-profile-card"
				entryPointReference={entryPointReference}
				runtimeProps={{
					cloudId,
					identityAccountId: accountId,
					hideStarButton,
					hideConversationStarters,
					onChatClick,
					onConversationStarterClick: handleConversationStarterClick,
					onViewProfile,
					onDuplicate,
					onCopyLink,
					onEdit,
					onDelete: handleDelete,
					renderCTA,
					renderMoreActions,
				}}
				fallback={<AgentProfileCardSkeleton />}
			/>
		</Suspense>
	);
}

/**
 * Profile card for a Rovo agent principal.
 *
 * Uses Relay entrypoint when gate is enabled, otherwise falls back to REST-based card.
 */
export function AgentCard(props: AgentCardProps): React.JSX.Element {
	if (fg('rovo_agent_profile_card_relay')) {
		return <AgentCardRelay {...props} />;
	}
	return <AgentProfileCardResourced {...props} />;
}
